import Axios from "axios";
import messages from "../errors/messages.en-IN.json";
import { requestScaleWeight, hasBrowserScale } from "../lib/scale/readScaleWeight";

/**
 * BUG-2568: one place for every weighing-scale read in the POS.
 *
 * The scale is read through the Printer Hardware service on the till PC
 * (`${hardwareController.imageUrl}getWeight`, reply `{ Weight: "0.255 kg" }`).
 * Every caller used to make this call itself and dropped every failure
 * silently, so the weight just stayed at 0 and the cashier could not tell
 * why. Callers now read through readScaleWeight() and show getScaleNotice()
 * for the reason it returns.
 *
 * Inside CW Browser the read goes over IPC instead (requestScaleWeight in
 * lib/scale/readScaleWeight.js): no Printer Hardware address is needed there,
 * and the IPC rejection says which reason applies (BUG-2662).
 *
 * Reasons:
 *   "noUrl"    - no Printer Hardware address on this till
 *   "noScale"  - this PC has no scale: CW Browser says "No scale configured", or the
 *                address answers 404 (no getWeight there, e.g. the CW Browser bridge on 8070)
 *   "service"  - no reply at all (Printer Hardware not running, refused, timed out)
 *   "scale"    - a scale is set up but gave no weight (scale off / cable out / port error)
 *   "unstable" - the scale replied, but the reading is still settling
 *   "invalid"  - the reply's weight is not a number (or the scale is overloaded)
 */

// The global axios timeout is 60 s (App.js); a scale read must not wait that long.
export const SCALE_TIMEOUT_MS = 3000;
// The popup polls every second; one dropped reply is not worth a message.
export const SCALE_FAILS_BEFORE_MESSAGE = 3;

export const getScaleUrl = (tillData) => tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl || "";

export const isScaleConfigured = (tillData) => tillData?.tillAccess?.cwrTill?.hardwareController?.weighingScale === "Y";

// A product whose quantity cannot be typed (restrictPosQtyEdit "Y") can only be weighed on the
// scale, so a scale failure leaves the cashier stuck. On any other product the cashier can type
// the weight, so the automatic reads stay quiet (Dadus types every weight on scale tills).
export const isScaleOnlyProduct = (product) => product?.restrictPosQtyEdit === "Y";

export const readScaleWeight = async (hwUrl, { method = "POST", data, headers } = {}) => {
  const browserScale = hasBrowserScale();
  if (!hwUrl && !browserScale) return { ok: false, reason: "noUrl" };
  let response;
  try {
    response = await requestScaleWeight(() =>
      Axios({
        url: `${hwUrl}getWeight`,
        method,
        timeout: SCALE_TIMEOUT_MS,
        ...(data !== undefined ? { data } : {}),
        ...(headers ? { headers } : {}),
      })
    );
  } catch (error) {
    // CW Browser: the IPC rejection says why (no scale configured, port error, reading not stable).
    if (browserScale) return { ok: false, reason: error?.scaleReason || "scale", error };
    // 404: the address answers but has no getWeight, so no scale is set up behind it (BUG-2662).
    if (error?.response?.status === 404) return { ok: false, reason: "noScale", error };
    // Any other HTTP error status means the service is up but could not read the scale.
    return { ok: false, reason: error?.response ? "scale" : "service", error };
  }
  const raw = response?.data?.Weight;
  if (raw === undefined || raw === null || String(raw).trim() === "") return { ok: false, reason: "scale" };
  const value = String(raw).replace(/kg/i, "").trim();
  const weight = parseFloat(value);
  if (isNaN(weight)) return { ok: false, reason: "invalid", raw: String(raw) };
  return { ok: true, weight, value, raw: String(raw) };
};

// What the cashier sees for a failed read: a short bold problem line and one plain action line
// (UX review v2). `short` is the billing-screen toast, away from the weight popup, where the
// "Printer Hardware" explanation is cut to the action. Read on use: tests mock the messages file.
export const getScaleNotice = (reason, { short = false } = {}) => {
  const text = messages?.scale?.[reason];
  if (!text) return { title: messages?.info?.weight_failed || "Failed to get weight.", action: "" };
  return { title: text.title, action: (short && text.short_action) || text.action };
};

// One line for a log or a plain-text place.
export const getScaleMessage = (reason, opts) => {
  const { title, action } = getScaleNotice(reason, opts);
  return action ? `${title}. ${action}` : title;
};
